import * as React from "react";
import { AlertCircle, ClipboardIcon, FilePenLine, Loader2, MoreHorizontal, MessageSquareIcon } from "lucide-react";
import { DriveRowMenu } from "./driveRowMenu";
import { useFeatureAccess } from "@/hooks/use-feature-access";
import { useRecentItems } from "@/hooks/use-add-recent-items";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "react-toastify";
import { Tooltip } from "@/components/ui/tooltip";
import { checkAuthoringEligibility } from "@/modules/documentAuthoring/documentAuthoring.api";
import { Item } from "@/types/fse";
import { Link, useNavigate } from "react-router";
import { DriveRowProgressbar } from "./driveRowProgressbar";
import { calculateUploadProgress } from "../modals/uploadModal/helpers";
import { getErrorMessage, isRejectionCode } from "../modals/uploadModal/errorMessages";
import { getPhaseLabel, PHASE_FALLBACK_LABEL } from "../modals/uploadModal/phaseLabels";
import { TxType } from "@/types/transaction";
import { ItemTxProgress } from "@/views/drive/types";
import { DriveRowButtonsFavorite } from "./driveRowButtonsFavorite";
import { getDriveItemProcessingState } from "./driveItemProcessingState";

interface Props {
  item: Item;
  progressForItem: Record<string, ItemTxProgress>;
  hiddenButtons?: Array<string>;
  currentFolderId: string | null;
  variant?: "default" | "compact";
}

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** A .docx whose eligibility was never checked (uploaded before Document Authoring existed). */
const isUncheckedDocx = (item: { name: string; content_type?: string | null; authoring_eligible?: boolean }) => {
  if (item.authoring_eligible) return false;
  const mime = (item.content_type ?? "").toLowerCase();
  return mime === DOCX_MIME || (!mime && item.name.toLowerCase().endsWith(".docx"));
};

export const DriveRowButtons: React.FC<Props> = ({
  item,
  progressForItem,
  currentFolderId,
  hiddenButtons = [],
  variant = "default",
}) => {
  const { hasAccess } = useFeatureAccess();
  let progressLabel: string = "";

  const { addRecentItem } = useRecentItems();

  const liveProgress: ItemTxProgress | undefined = progressForItem[item.id];
  const { progress, txType, isFailed, errorCode, isActionable } = getDriveItemProcessingState(item, liveProgress);
  const showFailureBadge = isFailed && !isRejectionCode(errorCode);
  const pendingVersion = item.type === "file" ? item.pending_version : null;
  const showAuthoring =
    isActionable && item.type === "file" && item.authoring_eligible === true && hasAccess("document_authoring");
  const showEnableAuthoring =
    isActionable && item.type === "file" && hasAccess("document_authoring") && isUncheckedDocx(item);
  const [checkingEligibility, setCheckingEligibility] = React.useState(false);
  const navigate = useNavigate();
  const enableAuthoring = async () => {
    setCheckingEligibility(true);
    try {
      const result = await checkAuthoringEligibility(item.id);
      if (result.eligible) {
        navigate(`/authoring/${item.id}`);
      } else if (result.reason === "original_missing") {
        toast.error("The original file of this document is no longer stored. Upload it again to use Authoring.");
      } else {
        toast.error("This document cannot be opened in Authoring. Upload it again as a .docx file.");
      }
    } catch {
      toast.error("Authoring could not be enabled for this document. Try again later.");
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleQAClick = () => {
    addRecentItem(item);
  };

  const actionButtonClassName =
    variant === "compact"
      ? "h-8 w-8 border-desert-sand-300 bg-ivory-mist-200 p-0 text-xs xl:w-auto xl:px-2"
      : "w-full text-xs sm:w-auto";
  const compactLabelClassName = variant === "compact" ? "sr-only xl:not-sr-only" : undefined;
  const compactIconClassName = variant === "compact" ? "h-4 w-4 xl:mr-1" : "mr-1 h-4 w-4";

  if (txType) {
    if (txType === TxType.UPLOAD) {
      progressLabel = "Uploading";
    } else if (txType === TxType.CONTRACT_FAMILY_GROUPING) {
      progressLabel = "Grouping";
    }
  }

  // Prefer the live ingestion phase ("Extracting text", ...) over the
  // coarse tx-type label. Live websocket wins; on first paint after a reload
  // (before the WS reconnects) fall back to the phase carried in the /ls payload.
  const restCurrentEvent = txType === TxType.UPLOAD ? item.transactions?.ingestion?.current_event : undefined;
  const phaseLabel = getPhaseLabel(liveProgress?.currentEvent ?? restCurrentEvent);
  if (phaseLabel) {
    progressLabel = phaseLabel;
  } else if (!progressLabel) {
    progressLabel = PHASE_FALLBACK_LABEL;
  }

  return (
    <div
      className={
        variant === "compact"
          ? "flex w-full flex-nowrap items-center justify-center gap-1.5"
          : "flex w-full flex-wrap items-center justify-end gap-1.5"
      }
    >
      {showFailureBadge ? (
        <Tooltip text={getErrorMessage(errorCode)}>
          <Badge variant="destructive">
            <AlertCircle className="mr-1 h-4 w-4" />
            Failed
          </Badge>
        </Tooltip>
      ) : (
        progress > 0 &&
        progress < 1 && (
          <DriveRowProgressbar progressLabel={progressLabel} progress={calculateUploadProgress(progress)} />
        )
      )}

      {pendingVersion && (
        <Tooltip text={`Version ${pendingVersion.version_no} is being processed`}>
          <Badge variant="review-amber" className="gap-1" data-testid="drive-pending-version">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            New version processing
          </Badge>
        </Tooltip>
      )}

      {!hiddenButtons.includes("favorite") && item.type === "folder" && <DriveRowButtonsFavorite item={item} />}

      {isActionable && hasAccess("reports") && !hiddenButtons.includes("snapShot") && (
        <Tooltip text="SnapShot" className={variant === "compact" ? "bg-white xl:hidden" : undefined}>
          <Button variant="secondary" size="sm" className={actionButtonClassName} asChild>
            <Link to={`/reports/${item.id}`} data-testid="drive-snapshot-link">
              <ClipboardIcon className={compactIconClassName} />
              <span className={compactLabelClassName}>SnapShot</span>
            </Link>
          </Button>
        </Tooltip>
      )}
      {isActionable && hasAccess("q_and_a") && !hiddenButtons.includes("qa") && (
        <Tooltip text="Q&A" className={variant === "compact" ? "bg-white xl:hidden" : undefined}>
          <Button variant="secondary" size="sm" className={actionButtonClassName} onClick={handleQAClick} asChild>
            <Link to={`/chat/${item.id}`}>
              <MessageSquareIcon className={compactIconClassName} />
              <span className={compactLabelClassName}>Q&A</span>
            </Link>
          </Button>
        </Tooltip>
      )}

      {showEnableAuthoring && !hiddenButtons.includes("authoring") && (
        <Tooltip
          text="Uploaded before Document Authoring was available. Click to enable it for this document."
          className="bg-white"
        >
          <Button
            variant="secondary"
            size="sm"
            className={actionButtonClassName}
            aria-label="Enable authoring"
            disabled={checkingEligibility}
            onClick={() => void enableAuthoring()}
          >
            <FilePenLine className={compactIconClassName} />
            <span className={compactLabelClassName}>{checkingEligibility ? "Checking…" : "Authoring"}</span>
          </Button>
        </Tooltip>
      )}

      {showAuthoring && !hiddenButtons.includes("authoring") && (
        <Tooltip text="Authoring" className={variant === "compact" ? "bg-white xl:hidden" : undefined}>
          <Button variant="secondary" size="sm" className={actionButtonClassName} asChild>
            <Link to={`/authoring/${item.id}`}>
              <FilePenLine className={compactIconClassName} />
              <span className={compactLabelClassName}>Authoring</span>
            </Link>
          </Button>
        </Tooltip>
      )}

      {!hiddenButtons.includes("options") && (
        <DriveRowMenu
          variant="empty"
          item={item}
          isFolder={item.type === "folder"}
          currentFolderId={currentFolderId}
          triggerAriaLabel={`Actions for ${item.name}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </DriveRowMenu>
      )}
    </div>
  );
};
